import type {
    DeliveryMethodType,
    CustomAddress,
    ProductApiResponse,
    RelatedProductsApiResponse,
} from '@libs/common-types';
import { logger } from '@libs/core';
import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

type GetProductBase = {
    id: string;
};

function isEmptyObject(obj: unknown) {
    return obj && typeof obj === 'object' && !Array.isArray(obj) ? Object.keys(obj).length === 0 : false;
}

export async function getProductBase({ id }: GetProductBase) {
    const url = CWEA_ENDPOINTS.product.getProductBase(id);
    logger.info(`[GET PRODUCT BASE]: ${url}`);

    // biome-ignore lint/complexity/noBannedTypes: empty object is a return type
    const data = await cweaClient.get<ProductApiResponse | {}>(url, {
        useOldFetch: true,
        additionalSuccessCodes: [404],
    });

    if (!data) {
        const error = new Error(`[GET PRODUCT BASE][ERROR] ${url}`) as Error & { statusCode: number };
        error.statusCode = 500;
        throw error;
    }

    if (isEmptyObject(data)) {
        const error = new Error(`[GET PRODUCT BASE][ERROR] ${url}`) as Error & { statusCode: number };
        error.statusCode = 404;
        throw error;
    }

    return data as ProductApiResponse;
}

type GetProductTailored = {
    deliveryMethodsToRetrieve: DeliveryMethodType[];
    storeId?: string;
    customAddress?: CustomAddress;
    variantSkus?: string[];
};

export async function getProductTailored<T>({
    storeId,
    customAddress,
    deliveryMethodsToRetrieve,
    variantSkus = [],
}: GetProductTailored): Promise<T> {
    const url = CWEA_ENDPOINTS.product.getProductTailored;
    logger.info(`[GET PRODUCT TAILORED]: ${url}`);

    const body = {
        storeId,
        customAddress,
        deliveryMethodsToRetrieve,
        variantSkus,
    };

    const result: Nullable<T> = await cweaClient.post(url, { body, useOldFetch: true });

    // Guard app layer from null response
    if (!result) {
        logger.error(`[GET PRODUCT TAILORED][Error]: ${url}`);

        throw new Error(`[GET PRODUCT TAILORED]: ${url} returned null`);
    }

    return result;
}

export async function getRelatedProducts(sku: string): Promise<RelatedProductsApiResponse> {
    const url = CWEA_ENDPOINTS.product.getRelatedProducts(sku);
    logger.info(`[GET RELATED PRODUCTS]: ${url}`);

    const result = await cweaClient.get<RelatedProductsApiResponse>(url, { useOldFetch: true });

    if (!result) {
        logger.error(`[GET RELATED PRODUCTS][Error]: ${url}`);
        throw new Error(`[GET RELATED PRODUCTS]: ${url} returned null`);
    }

    return result;
}
